/**
 * Marketplace Page - 마켓플레이스
 * 
 * 노드(도구)를 설치/관리하는 페이지
 * 설치된 노드만 워크플로우 빌더에서 사용 가능
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient, authApi } from '../api';
import { Topbar } from '../components/layout';
import { Badge, Button, Modal, SearchInput, Tabs, useToast } from '../components/ui';
import CheckIcon from '../assets/icons/common/check.svg?react';
import DownloadIcon from '../assets/icons/common/download.svg?react';
import TrashIcon from '../assets/icons/common/trash.svg?react';
import SettingIcon from '../assets/icons/common/setting.svg?react';
import { EmailConnectorModal } from '../components/workflow/EmailConnectorModal';
// 카드 아이콘 - 선 아이콘(?react, 글자색 따라감) / 브랜드 로고(이미지, 원래 색 유지)
// assets/icons/marketplace 의 같은 파일명으로 교체하면 반영
import MailIcon from '../assets/icons/marketplace/mail.svg?react';
import ScheduleIcon from '../assets/icons/marketplace/schedule.svg?react';
import ChatIcon from '../assets/icons/marketplace/chat.svg?react';
import ManualRunIcon from '../assets/icons/marketplace/manual-run.svg?react';
import AiIcon from '../assets/icons/marketplace/ai.svg?react';
import DocSearchIcon from '../assets/icons/marketplace/doc-search.svg?react';
import ConditionIcon from '../assets/icons/marketplace/condition.svg?react';
import ReplyIcon from '../assets/icons/marketplace/reply.svg?react';
import WebSearchIcon from '../assets/icons/marketplace/web-search.svg?react';
import SummarizeIcon from '../assets/icons/marketplace/summarize.svg?react';
import McpIcon from '../assets/icons/marketplace/mcp.svg?react';
import googleDriveLogo from '../assets/icons/marketplace/google-drive.svg';
import gmailLogo from '../assets/icons/marketplace/gmail.svg';
import slackLogo from '../assets/icons/marketplace/slack.svg';
import notionLogo from '../assets/icons/marketplace/notion.svg';
import naverLogo from '../assets/icons/marketplace/naver.svg';
import emailConnectorLogo from '../assets/icons/marketplace/email-connector.svg';
import dropboxLogo from '../assets/icons/marketplace/dropbox.svg';
import './MarketplacePage.css';

// 아이템 id → 카드 아이콘 (없으면 기존 이모지 icon 표시)
type SvgIcon = React.FC<React.SVGProps<SVGSVGElement>>;
const ITEM_ICONS: Record<string, SvgIcon | string> = {
  mail: MailIcon,
  schedule: ScheduleIcon,
  manual_run: ManualRunIcon,
  chat_start: ChatIcon,
  ai: AiIcon,
  search: DocSearchIcon,
  cond: ConditionIcon,
  reply: ReplyIcon,
  ddg: WebSearchIcon,
  summarize: SummarizeIcon,
  mcp: McpIcon,
  drive: googleDriveLogo,
  gmail: gmailLogo,
  slack: slackLogo,
  notion: notionLogo,
  naver: naverLogo,
  email: emailConnectorLogo,
  dropbox: dropboxLogo,
};

// 카테고리 → 아이콘 칸 색 (트리거 = info, 액션 = primary, 커넥터 = 흰 바탕 + 테두리)
const ICON_COLOR: Record<MarketplaceItem['category'], string> = {
  trigger: 'item-card-icon--info',
  chatbot: 'item-card-icon--info',
  action: 'item-card-icon--primary',
  connector: 'item-card-icon--default',
};

// 검색창 주변 장식 로고
const HERO_LOGOS = [
  { src: naverLogo, cls: 'p1' },
  { src: slackLogo, cls: 'p2' },
  { src: googleDriveLogo, cls: 'p3' },
  { src: notionLogo, cls: 'p4' },
  { src: gmailLogo, cls: 'p5' },
];

// 노드 타입 정의
export interface MarketplaceItem {
  id: string;
  title: string;
  desc: string;
  category: 'trigger' | 'chatbot' | 'action' | 'connector';
  status: 'installed' | 'available' | 'custom_needed';
  nodeType: string; // 워크플로우 빌더에서 사용할 노드 타입
  icon: string; // 노드 아이콘
}

// 마켓플레이스 아이템 데이터
const MARKETPLACE_ITEMS: MarketplaceItem[] = [
  // 트리거
  // { id: 'mail', title: '메일 도착', desc: '새 메일이 도착하면 워크플로우를 시작합니다.', category: 'trigger', status: 'available', nodeType: 'mail-trigger', icon: '📧' },
  // { id: 'file_upload', title: '파일 업로드', desc: '파일이 업로드되면 워크플로우를 시작합니다.', category: 'trigger', status: 'available', nodeType: 'file-trigger', icon: '📁' },
  // { id: 'webhook', title: '웹훅 수신', desc: '외부 시스템에서 API 호출이 오면 워크플로우를 시작합니다.', category: 'trigger', status: 'available', nodeType: 'webhook-trigger', icon: '🔗' },
  { id: 'schedule', title: '스케줄', desc: '지정한 시간이 되면 워크플로우를 시작합니다.', category: 'trigger', status: 'available', nodeType: 'schedule-trigger', icon: '⏰' },
  { id: 'manual_run', title: '수동 실행', desc: '사용자가 직접 실행 버튼을 눌러 워크플로우를 시작합니다.', category: 'trigger', status: 'available', nodeType: 'manual-trigger', icon: '▶️' },
  
  // 챗봇
  { id: 'chat_start', title: '채팅 시작', desc: '사용자가 챗봇에 말을 걸면 워크플로우가 시작됩니다.', category: 'trigger', status: 'available', nodeType: 'trigger', icon: '💬' },
  
  // 액션
  { id: 'ai', title: 'AI 응답', desc: 'AI가 내용을 파악하거나 답변을 생성합니다.', category: 'action', status: 'available', nodeType: 'llm', icon: '🤖' },
  { id: 'search', title: '문서 검색', desc: '연결된 지식베이스에서 관련 내용을 찾아옵니다.', category: 'action', status: 'available', nodeType: 'knowledge-retrieval', icon: '🔍' },
  { id: 'cond', title: '판단', desc: '조건에 따라 다음 경로를 나눕니다.', category: 'action', status: 'available', nodeType: 'condition', icon: '🔀' },
  // { id: 'reply', title: '답변 전송', desc: '생성된 답변을 질문한 사용자에게 바로 돌려줍니다.', category: 'action', status: 'installed', nodeType: 'output', icon: '💬' },
  // { id: 'notify', title: '담당자 전송', desc: '담당자에게 알림을 보냅니다.', category: 'action', status: 'available', nodeType: 'notify', icon: '🔔' },

  // { id: 'data_transform', title: '데이터 변환', desc: '데이터 형식을 변환하거나 가공합니다.', category: 'action', status: 'available', nodeType: 'data-transform', icon: '🔄' },
  // { id: 'email_send', title: '이메일 발송', desc: '지정한 수신자에게 이메일을 보냅니다.', category: 'action', status: 'available', nodeType: 'email-send', icon: '✉️' },
  // { id: 'delay', title: '대기', desc: '지정한 시간만큼 대기 후 다음 단계로 진행합니다.', category: 'action', status: 'available', nodeType: 'delay', icon: '⏳' },
  // { id: 'loop', title: '반복', desc: '조건이 충족될 때까지 특정 작업을 반복합니다.', category: 'action', status: 'available', nodeType: 'loop', icon: '🔁' },
  // { id: 'http_call', title: 'HTTP 요청', desc: '외부 API에 HTTP 요청을 보내고 응답을 받습니다.', category: 'action', status: 'available', nodeType: 'http-call', icon: '📡' },
  // { id: 'summarize', title: '요약', desc: '긴 텍스트나 파일을 핵심만 추려 요약합니다.', category: 'action', status: 'available', nodeType: 'summarize', icon: '📝' },
  // { id: 'file_gen', title: '문서 생성', desc: '템플릿 기반으로 보고서나 문서를 생성합니다.', category: 'action', status: 'available', nodeType: 'file-gen', icon: '📄' },
  
  // 커넥터
  { id: 'ddg', title: '웹 검색', desc: '외부 웹에서 정보를 찾아옵니다.', category: 'connector', status: 'available', nodeType: 'web-search', icon: '🌐' },
  { id: 'drive', title: 'Google Drive', desc: 'Drive에 있는 문서를 가져오거나 저장합니다.', category: 'connector', status: 'available', nodeType: 'google-drive', icon: '📂' },
  // { id: 'gmail', title: 'Gmail', desc: 'Gmail 메일을 읽거나 발송합니다.', category: 'connector', status: 'available', nodeType: 'gmail', icon: '📬' },
  { id: 'email', title: '이메일 커넥터', desc: 'SMTP/IMAP을 통해 어떤 메일 서비스든 발송·수신합니다.', category: 'connector', status: 'available', nodeType: 'email', icon: '✉️' },
  { id: 'slack', title: 'Slack', desc: 'Slack 메시지를 보내거나 받습니다.', category: 'connector', status: 'available', nodeType: 'slack', icon: '💬' },
  { id: 'notion', title: 'Notion', desc: 'Notion 페이지를 읽거나 새 페이지를 생성합니다.', category: 'connector', status: 'available', nodeType: 'notion', icon: '📓' },
  // { id: 'naver', title: '네이버 메일', desc: '네이버 메일을 읽거나 발송합니다.', category: 'connector', status: 'available', nodeType: 'naver', icon: '🟢' },
  { id: 'mcp', title: 'MCP 서버', desc: 'MCP 서버 URL을 등록하여 외부 도구를 워크플로우에서 직접 호출합니다.', category: 'connector', status: 'available', nodeType: 'mcp', icon: '🔌' },
  { id: 'dropbox', title: 'Dropbox', desc: 'Dropbox에 있는 파일을 가져오거나 저장합니다.', category: 'connector', status: 'available', nodeType: 'dropbox', icon: '📦' },
  // { id: 'jira', title: 'Jira', desc: 'Jira 이슈를 생성하거나 상태를 업데이트합니다.', category: 'connector', status: 'available', nodeType: 'jira', icon: '🎫' },
  // { id: 'sheets', title: 'Google Sheets', desc: '스프레드시트를 읽거나 데이터를 기록합니다.', category: 'connector', status: 'available', nodeType: 'google-sheets', icon: '📊' },
  // { id: 'erp', title: '사내 ERP', desc: '사내 ERP 시스템의 데이터를 조회합니다.', category: 'connector', status: 'custom_needed', nodeType: 'erp', icon: '🏢' },
  // { id: 'wiki', title: '사내 위키', desc: '사내 위키·문서함에서 검색합니다.', category: 'connector', status: 'custom_needed', nodeType: 'wiki', icon: '📚' },
];

const CATEGORIES = [
  { value: 'all', label: '전체' },
  { value: 'trigger', label: '트리거' },
  { value: 'action', label: '액션' },
  { value: 'connector', label: '커넥터' },
];

// 설치 목록 localStorage 캐시 키 — 사용자별로 분리 (계정 전환 시 섞이지 않도록)
const storageKey = () => {
  const uid = authApi.getUser()?.id || 'anon';
  return `marketplace_installed_${uid}`;
};

export function MarketplacePage() {
  const { showToast } = useToast();
  const { category: urlCategory } = useParams<{ category?: string }>();
  const navigate = useNavigate();
  const [items, setItems] = useState<MarketplaceItem[]>([]);
  const [activeFilter, setActiveFilter] = useState('all');

  // 탭 전환 → URL 이동 (사이드바 메뉴 하이라이트 동기화)
  const CATEGORY_URL: Record<string, string> = {
    all: '/marketplace',
    trigger: '/marketplace/triggers',
    action: '/marketplace/actions',
    connector: '/marketplace/connectors',
  };
  const handleTabChange = (value: string) => {
    setActiveFilter(value);
    navigate(CATEGORY_URL[value] || '/marketplace');
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [uninstallConfirmId, setUninstallConfirmId] = useState<string | null>(null);

  // URL 파라미터로 카테고리 설정
  useEffect(() => {
    if (urlCategory) {
      const categoryMap: Record<string, string> = {
        triggers: 'trigger',
        chatbots: 'chatbot',
        actions: 'action',
        connectors: 'connector',
      };
      setActiveFilter(categoryMap[urlCategory] || 'all');
    } else {
      setActiveFilter('all');
    }
  }, [urlCategory]);

  useEffect(() => {
    loadItems();
  }, []);

  const applyInstalledIds = (installedIds: string[]) => {
    const loadedItems = MARKETPLACE_ITEMS.map((item) => ({
      ...item,
      status: installedIds.includes(item.id) ? 'installed' : item.status,
    })) as MarketplaceItem[];
    setItems(loadedItems);
    // getInstalledNodes(빌더 팔레트)가 읽는 사용자별 캐시 갱신
    localStorage.setItem(storageKey(), JSON.stringify(installedIds));
  };

  const loadItems = async () => {
    // 사용자별 설치 목록을 서버(DDB)에서 조회
    try {
      const res = await apiClient.get<{ success: boolean; installedIds: string[] }>('/api/marketplace/installed');
      const serverIds = res.data.installedIds || [];
      if (serverIds.length === 0) {
        // 신규 사용자 - 기본값으로 초기화하고 DB에 저장
        const DEFAULT_IDS = ['chat_start', 'manual_run', 'ai', 'search', 'cond'];
        applyInstalledIds(DEFAULT_IDS);
        await apiClient.put('/api/marketplace/installed', { installedIds: DEFAULT_IDS }).catch(() => {});
      } else {
        applyInstalledIds(serverIds);
      }
    } catch {
      // 조회 실패 시 캐시로 폴백
      const cached = localStorage.getItem(storageKey());
      applyInstalledIds(cached ? JSON.parse(cached) : ['chat_start', 'manual_run', 'ai', 'search', 'cond']);
    }
  };

  const saveInstalledIds = async (items: MarketplaceItem[]) => {
    const installedIds = items.filter((i) => i.status === 'installed').map((i) => i.id);
    localStorage.setItem(storageKey(), JSON.stringify(installedIds));
    try {
      await apiClient.put('/api/marketplace/installed', { installedIds });
    } catch {
      /* 저장 실패해도 캐시는 유지 */
    }
  };

  const handleInstall = (itemId: string) => {
    const updatedItems = items.map((item) =>
      item.id === itemId ? { ...item, status: 'installed' as const } : item
    );
    setItems(updatedItems);
    saveInstalledIds(updatedItems);
    
    const item = items.find((i) => i.id === itemId);
    showToast('success', `"${item?.title}" 도구가 설치되었습니다.`);
  };

  const handleUninstall = (itemId: string) => {
    setUninstallConfirmId(itemId);
  };

  const confirmUninstall = () => {
    if (!uninstallConfirmId) return;
    const itemId = uninstallConfirmId;
    const item = items.find((i) => i.id === itemId);
    setUninstallConfirmId(null);
    
    const updatedItems = items.map((item) =>
      item.id === itemId ? { ...item, status: 'available' as const } : item
    );
    setItems(updatedItems);
    saveInstalledIds(updatedItems);
    
    showToast('success', `"${item?.title}" 도구가 제거되었습니다.`);
  };

  // 검색어만 적용한 아이템 (탭별 개수 집계용 - 카테고리 필터는 제외)
  const searchedItems = items.filter(
    (item) => !searchQuery || item.title.toLowerCase().includes(searchQuery.toLowerCase()) || item.desc.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // 탭(카테고리)별 개수 - '전체'는 전체 수, 나머지는 해당 카테고리 수
  const tabCounts: Record<string, number> = {
    all: searchedItems.length,
    trigger: searchedItems.filter((i) => i.category === 'trigger').length,
    action: searchedItems.filter((i) => i.category === 'action').length,
    connector: searchedItems.filter((i) => i.category === 'connector').length,
  };

  // 필터링된 아이템
  const filteredItems = items.filter((item) => {
    if (searchQuery && !item.title.toLowerCase().includes(searchQuery.toLowerCase()) && !item.desc.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (activeFilter !== 'all' && item.category !== activeFilter) {
      return false;
    }
    return true;
  });

  // 카테고리별 그룹핑
  const groupedItems = {
    trigger: filteredItems.filter((i) => i.category === 'trigger'),
    chatbot: filteredItems.filter((i) => i.category === 'chatbot'),
    action: filteredItems.filter((i) => i.category === 'action'),
    connector: filteredItems.filter((i) => i.category === 'connector'),
  };

  const renderIcon = (item: MarketplaceItem) => {
    const icon = ITEM_ICONS[item.id];
    if (!icon) return item.icon;
    if (typeof icon === 'string') return <img src={icon} alt="" />;
    const Icon = icon;
    return <Icon />;
  };

  // 설치 = ↓ 아이콘 버튼, 제거 = 휴지통 아이콘 버튼
  const renderAction = (item: MarketplaceItem) => {
    if (item.status === 'installed') {
      return (
        <div style={{ display: 'flex', gap: 4 }}>
          {/* 이메일 커넥터는 설치 후 설정 버튼 추가 */}
          {item.id === 'email' && (
            <button
              type="button"
              className="item-card-icon-btn"
              onClick={() => setEmailModalOpen(true)}
              title="커넥터 설정"
              aria-label="이메일 커넥터 설정"
            >
              <SettingIcon width={16} height={16} fill="currentColor" />
            </button>
          )}
          <button type="button" className="item-card-icon-btn" onClick={() => handleUninstall(item.id)} title="제거" aria-label={`${item.title} 제거`}>
            <TrashIcon />
          </button>
        </div>
      );
    }
    if (item.status === 'available') {
      return (
        <button type="button" className="item-card-icon-btn" onClick={() => handleInstall(item.id)} title="설치" aria-label={`${item.title} 설치`}>
          <DownloadIcon />
        </button>
      );
    }
    return <Button size="sm" color="gray" appearance="outline" className="item-card-action-btn" disabled>개발 필요</Button>;
  };

  // 설치됨 = 보라 테두리 · 옅은 배경 + 아이콘 모서리 체크 배지 / 미설치 = 기본 카드 + 보라 설치 버튼
  const STATUS_CLASS: Record<MarketplaceItem['status'], string> = {
    installed: ' market-card--installed',
    available: ' market-card--available',
    custom_needed: ' item-card--dashed',
  };

  const renderItem = (item: MarketplaceItem) => (
    <div key={item.id} className={`item-card${STATUS_CLASS[item.status]}`}>
      <span className={`item-card-icon ${ICON_COLOR[item.category]}`}>
        {renderIcon(item)}
        {item.status === 'installed' && (
          <span className="market-card-check" aria-hidden="true"><CheckIcon /></span>
        )}
      </span>
      <div className="item-card-body">
        <div className="item-card-title">{item.title}</div>
        <div className="item-card-desc">{item.desc}</div>
        <div className="item-card-meta">
          {item.status === 'installed' ? (
            <Badge color="success">설치됨</Badge>
          ) : item.status === 'available' ? (
            <Badge color="gray">미설치</Badge>
          ) : (
            <Badge color="medium">커스텀 필요</Badge>
          )}
        </div>
      </div>
      <div className="item-card-action">{renderAction(item)}</div>
    </div>
  );

  return (
    <>
      <Topbar
        title="마켓플레이스"
        description="필요한 도구를 찾아서 설치하세요"
      />

      <div className="page-content">
        <div className="page-content-inner market-container">
          {/* 검색 영역 - 주변 로고는 장식 */}
          <div className="market-hero">
            {HERO_LOGOS.map((l) => (
              <span key={l.cls} className={`market-hero-logo ${l.cls}`} aria-hidden="true">
                <img src={l.src} alt="" />
              </span>
            ))}

            <SearchInput
              className="market-search"
              placeholder="도구 이름 · 설명으로 검색 (예: AI, 검색, Slack)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size='lg'
            />

            {/* 안내 */}
			<div className="market-legend">
			   <span>AI 응답, 웹 검색, 이메일 발송 등 업무 자동화에 필요한 도구를 설치하고 워크플로우에 연결하세요.</span>
			</div>
          </div>

          <div className="market-panel">
          {/* 카테고리 탭 (공통 Tabs) */}
          <Tabs
            items={CATEGORIES.map((cat) => ({
              key: cat.value,
              label: (
                <>
                  {cat.label}
                  <span className="tab-count">{tabCounts[cat.value] ?? 0}</span>
                </>
              ),
            }))}
            active={activeFilter}
            onChange={handleTabChange}
          />

          {/* 카테고리별 섹션 */}
          {activeFilter === 'all' ? (
            <>
              {groupedItems.trigger.length > 0 && (
                <div className="market-section">
                  <div className="market-section-label">트리거</div>
                  <div className="card-grid">{groupedItems.trigger.map(renderItem)}</div>
                </div>
              )}
              {groupedItems.chatbot.length > 0 && (
                <div className="market-section">
                  <div className="market-section-label">챗봇</div>
                  <div className="card-grid">{groupedItems.chatbot.map(renderItem)}</div>
                </div>
              )}
              {groupedItems.action.length > 0 && (
                <div className="market-section">
                  <div className="market-section-label">액션</div>
                  <div className="card-grid">{groupedItems.action.map(renderItem)}</div>
                </div>
              )}
              {groupedItems.connector.length > 0 && (
                <div className="market-section">
                  <div className="market-section-label">커넥터</div>
                  <div className="card-grid">{groupedItems.connector.map(renderItem)}</div>
                </div>
              )}
            </>
          ) : (
            <div className="market-section">
              <div className="card-grid">{filteredItems.map(renderItem)}</div>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* 이메일 커넥터 설정 모달 */}
      <EmailConnectorModal
        isOpen={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onSaved={() => setEmailModalOpen(false)}
      />

      {/* 도구 제거 확인 모달 */}
      <Modal
        isOpen={!!uninstallConfirmId}
        onClose={() => setUninstallConfirmId(null)}
        title="도구 제거"
        size="sm"
        style={{ maxWidth: 360 }}
        bodyClassName="chat-confirm-body"
        footerLeft={
          <Button color="gray" appearance="outline" onClick={() => setUninstallConfirmId(null)}>취소</Button>
        }
        footerRight={
          <Button color="error" onClick={confirmUninstall}>제거</Button>
        }
      >
        "{items.find(i => i.id === uninstallConfirmId)?.title}" 도구를 제거하시겠습니까?
      </Modal>
    </>
  );
}

// MARKETPLACE_ITEMS export (복사하기 노드 체크용)
export { MARKETPLACE_ITEMS };

// 설치된 노드 목록 가져오기 (워크플로우 빌더에서 사용)
export function getInstalledNodes(): MarketplaceItem[] {
  const savedInstalled = localStorage.getItem(storageKey());
  // 캐시가 있으면 그대로 사용(서버 설치 목록). 캐시가 없을 때만 기본 세트 폴백
  //  (커넥터는 기본 제공 안 함 — 직접 설치해야 팔레트에 노출)
  const installedIds: string[] = savedInstalled
    ? JSON.parse(savedInstalled)
    : ['chat_start', 'manual_run', 'ai', 'search', 'cond'];

  return MARKETPLACE_ITEMS.filter((item) => installedIds.includes(item.id));
}

export default MarketplacePage;
